"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useTheme } from "@/app/theme-provider";
import { IMG_URL } from "@/app/config";
import useFetch from "@/app/useFetch";

const FooterLogoCarousel = dynamic(() => import("@/Components/FooterLogoCarousel"), {
  ssr: false,
  loading: () => null,
});

const QUICK_LINKS = {
  en: [
    ["/gallery", "Gallery"],
    ["/tenders", "Tenders"],
    ["/downloads/orders/government-orders", "Government orders"],
    ["/wings/courts-tribunals-wings", "Courts and tribunals"],
    ["/downloads/act-and-rules", "Acts and Rules"],
    ["/wings/special-cells", "Special cells"],
    ["/wings/siu-wings", "Special investigation units"],
    ["/right-to-information", "Right to information"],
    ["/press-release", "Press Release"],
    ["/downloads/seniority-list", "Seniority List"],
    ["/wings/ranges-and-units", "Ranges and Units"],
    ["/downloads/forms", "Forms"],
    ["/vigilance/who-is-who", "Who is who"],
    ["/wings/legal-advisor", "Legal wings"],
    ["/faq", "FAQ"],
    ["https://old.vigilance.kerala.gov.in", "Old Website", true],
  ],
  ml: [
    ["/gallery", "ഗാലറി"],
    ["/tenders", "ടെൻഡറുകൾ"],
    ["/downloads/orders/government-orders", "സർക്കാർ ഉത്തരവുകൾ"],
    ["/wings/courts-tribunals-wings", "കോടതികളും ട്രൈബ്യൂണലുകളും"],
    ["/downloads/act-and-rules", "നിയമങ്ങളും നിയമങ്ങളും"],
    ["/wings/special-cells", "പ്രത്യേക സെല്ലുകൾ"],
    ["/wings/siu-wings", "പ്രത്യേക അന്വേഷണ യൂണിറ്റുകൾ"],
    ["/right-to-information", "വിവരാവകാശം"],
    ["/press-release", "പ്രസ് റിലീസ്"],
    ["/downloads/seniority-list", "സീനിയോറിറ്റി ലിസ്റ്റ്"],
    ["/wings/ranges-and-units", "ശ്രേണികളും യൂണിറ്റുകളും"],
    ["/downloads/forms", "ഫോമുകൾ"],
    ["/vigilance/who-is-who", "യൂണിറ്റ് മേധാവികള്‍"],
    ["/wings/legal-advisor", " നിയമ വിഭാഗം"],
    ["/faq", "പതിവുചോദ്യങ്ങൾ"],
    ["https://old.vigilance.kerala.gov.in", "പഴയ വെബ്സൈറ്റ്", true],
  ],
};

const chunkIntoColumns = (items, columns) =>
  Array.from({ length: columns }, (_, columnIndex) =>
    items.filter((_, itemIndex) => itemIndex % columns === columnIndex)
  );

export default function Footer() {
  const { theme, language } = useTheme();
  const footerRef = useRef(null);
  const [shouldLoadFooterData, setShouldLoadFooterData] = useState(false);
  const [shouldLoadLogoData, setShouldLoadLogoData] = useState(false);
  useEffect(() => {
    const footerNode = footerRef.current;
    if (!footerNode) {
      return undefined;
    }

    const loadFooterData = () => setShouldLoadFooterData(true);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadFooterData();
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(footerNode);

    let idleId;
    let timeoutId;

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(loadFooterData, { timeout: 1200 });
    } else {
      timeoutId = window.setTimeout(loadFooterData, 700);
    }

    return () => {
      observer.disconnect();

      if (idleId) {
        window.cancelIdleCallback(idleId);
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldLoadFooterData) {
      return undefined;
    }

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(
        () => setShouldLoadLogoData(true),
        { timeout: 1800 }
      );

      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(() => setShouldLoadLogoData(true), 1200);
    return () => window.clearTimeout(timeoutId);
  }, [shouldLoadFooterData]);

  const { data: logo } = useFetch(
    shouldLoadLogoData ? "/website/homepage-logo-slider" : null,
    [],
    []
  );

  const { data: footAddres } = useFetch(
    shouldLoadFooterData ? "/website/footer-sections" : null,
    [],
    [],
    {}
  );

  const { data: footsocialLinks } = useFetch(
    shouldLoadFooterData ? "/website/footer-social-medias" : null,
    [],
    []
  );

  const quickLinkColumns = useMemo(() => {
    const localizedLinks = QUICK_LINKS[language] || QUICK_LINKS.en;
    return chunkIntoColumns(localizedLinks, 4);
  }, [language]);

  return (
    <footer ref={footerRef} className="mt-40">
      <section className="cmpad">
        {logo.length > 0 ? (
          <div className="footer-1-box">
            <div className="main-footer-box">
              <FooterLogoCarousel logos={logo} />
            </div>
          </div>
        ) : null}

        <div className="mt-3">
          <div className="row">
            <div className="col-lg-3 col-md-4 col-12 mt-3 mb-4">
              <div>
                {theme === "light" ? (
                  <img src="/logo/lightlogo.svg" alt="Logo" className="logo" />
                ) : (
                  <img src="/logo/logonew.svg" alt="Logo" className="logo" />
                )}

                <div className="d-flex mt-4 gap-2">
                  <p className="footer-text ">{footAddres?.address}</p>
                </div>

                <div>
                  <a
                    href="mailto:vig.vacb@kerala.gov.in"
                    className="d-flex gap-2 align-items-center text-decoration-none"
                  >
                    <img
                      src="/icons/email.svg"
                      alt="footer Logos"
                      className="email"
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="footer-link-text">{footAddres?.footer_email}</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="col-lg-1 col-md-1 col-0 d-none d-lg-block d-md-block">&nbsp;</div>

            <div className="col-lg-8 col-md-8 col-12 mt-3 mb-4">
              <div className="row">
                <div>
                  <p className="footer-head mb-3">
                    {language === "en" ? "Quick Links" : "ദ്രുത ലിങ്കുകൾ"}
                  </p>
                </div>

                {quickLinkColumns.map((column, columnIndex) => (
                  <div key={columnIndex} className="col-lg-3 col-md-3 col-6 mt-1">
                    {column.map(([href, label, external]) => (
                      <div key={href} className="f-p-b ">
                        {external ? (
                          <Link target="blank" href={href} className="footer-link-text">
                            {label}
                          </Link>
                        ) : (
                          <Link href={href} className="footer-link-text">
                            {label}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="row">
            <div className="col-lg-3 col-md-4 col-12">
              <span className="footer-head">{language === "en" ? "Contact" : "ബന്ധപ്പെടുക"}</span>
              <div className="mt-3 d-flex align-items-center gap-3">
                <div className="call">
                  <img
                    src="/icons/call.svg"
                    alt="phone"
                    width="18"
                    height="18"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <div>
                  <div>
                    <a aria-label="call" href="tel:04712305393" className="call-link">
                      {footAddres?.contact_no_one}
                    </a>
                  </div>
                  <div>
                    <a aria-label="call" href="tel:04712305393" className="call-link">
                      {footAddres?.contact_no_two}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-1 col-md-0 col-0 d-none d-lg-block">&nbsp;</div>

            <div className="col-lg-4 col-md-4 col-12">
              <span className="footer-head">{language === "en" ? "Follow Us" : "ഫോളോ ചെയ്യൂ"}</span>
              <div className="icon-wrapper mt-3">
                {footsocialLinks?.map((item, index) => (
                  <a
                    key={item?.id ?? index}
                    href={item?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=""
                  >
                    <img
                      src={`${IMG_URL}${item.photo}`}
                      alt="photo"
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                ))}
              </div>
            </div>

            <div className="col-lg-4 col-md-4 col-12">
              <span style={{ color: "orange" }} className="footer-head">
                {language === "en" ? "Visitors" : "സന്ദർശകർ"}
              </span>
              <div className="icon-wrapper mt-3">
                <p style={{ color: "orange" }}>{footAddres?.visitors}</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-4 col-12 d-none">
              <h6 className="footer-head">Get Latest Updates</h6>
              <div className="subscribe-section mt-3">
                <form className="subscribe-form">
                  <input
                    type="email"
                    className="subscribe-input"
                    placeholder="Email Address"
                    required
                  />
                  <button type="submit" className="subscribe-button">
                    Subscribe
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="copy-box mt-40">
          <div className="row">
            <div className="col-lg-6 col-md-12 col-12">
              <div className=" h-100 d-flex align-items-center flex-wrap gap-3"></div>
            </div>

            <div className="col-lg-6 col-md-12 col-12">
              <p className="text-copy mb-1 text-start text-md-end">
                © 2025 Kerala Vigilance and Anti-Corruption Bureau. All Rights Reserved.
              </p>
              <p className="mb-0 text-start text-md-end">
                <a href="#" className="text-copy text-start text-md-end">
                  Powered by SRV InfoTech
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </footer>
  );
}
